// client/src/pages/seo/RestaurantWebsiteDe.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SeoRelatedLinks from "../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "check",
    label: "Braucht Ihr Restaurant eine bessere Website?",
  },
  {
    id: "definition",
    label: "Was sollte eine Restaurant-Website heute leisten?",
  },
  {
    id: "visibility",
    label: "Warum Online-Sichtbarkeit für Restaurants wichtig ist",
  },
  {
    id: "menu",
    label: "Die Speisekarte ist oft der wichtigste Teil einer Restaurant-Website",
  },
  {
    id: "trust",
    label: "Eine Website schafft Vertrauen, bevor der Gast ankommt",
  },
  {
    id: "reservation",
    label: "Website, Speisekarte und Reservierung sollten zusammenarbeiten",
  },
  {
    id: "mistakes",
    label: "Häufige Fehler bei Restaurant-Websites",
  },
  {
    id: "small",
    label: "Brauchen auch kleine Restaurants eine Website?",
  },
  {
    id: "bestplace",
    label: "Wie Best Place beim Aufbau einer Restaurant-Website hilft",
  },
  {
    id: "faq",
    label: "FAQ zu Restaurant-Websites",
  },
];

const checklist = [
  "Ihr Restaurant hat keine Website oder nur eine sehr alte.",
  "Ihre Speisekarte ist online schwer zu finden.",
  "Gäste fragen häufig nach Öffnungszeiten, Preisen oder der Speisekarte.",
  "Sie möchten QR-Code, Speisekarte und Reservierung an einem Ort verbinden.",
  "Sie möchten weniger von Social-Media-Plattformen abhängig sein.",
  "Ihr Restaurant soll bei Google und in der Online-Suche leichter verständlich sein.",
  "Sie möchten Fotos, Angebote oder aktuelle Informationen einfacher zeigen.",
  "Sie möchten Gästen helfen, sich vor dem Besuch zu entscheiden.",
  "Sie möchten eine einfache Seite ohne kompliziertes Agenturprojekt.",
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function getResult(score) {
  if (score <= 3) {
    return {
      title: "Eine einfache Online-Präsenz kann vorerst ausreichen",
      text:
        "Wenn Gäste Ihr Restaurant bereits leicht finden und die wichtigsten Informationen online klar sind, ist eine neue Website vielleicht noch nicht dringend.",
      tone: "low",
    };
  }

  if (score <= 6) {
    return {
      title: "Eine Restaurant-Website wäre sinnvoll",
      text:
        "Ihr Restaurant kann von einer klaren Website profitieren: Speisekarte, Öffnungszeiten, Kontakt, Reservierung und QR-Code können an einem Ort zusammenkommen.",
      tone: "medium",
    };
  }

  return {
    title: "Eine Website kann Ihr digitales Zentrum werden",
    text:
      "Mehrere Punkte sprechen dafür, dass Ihr Restaurant eine Website als zentralen digitalen Ort nutzen sollte: Sichtbarkeit, Speisekarte, Reservierung, Vertrauen und bessere Orientierung für Gäste.",
    tone: "high",
  };
}

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Idee für Illustration">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idee für Illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function RestaurantWebsiteDe() {
  const [checked, setChecked] = useState([]);

  const score = checked.length;
  const result = useMemo(() => getResult(score), [score]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", "de");
    document.title =
      "Restaurant-Website für Restaurants: Speisekarte, Reservierung und Vertrauen | Best Place";
  }, []);

  const toggle = (item) => {
    setChecked((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
    );
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="de"
        quickTarget="check"
        quickLabel="Kurzer Check"
        langLinks={{
          en: "/en/restaurant-website-for-restaurants",
          de: "/de/restaurant-website",
          no: "/no/restaurant-nettside",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Restaurant-Website für Restaurants: Speisekarte, Reservierung und
              Vertrauen an einem Ort
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Eine Restaurant-Website ist nicht nur eine digitale Visitenkarte.
                Sie kann der Ort sein, an dem Gäste Ihre Speisekarte ansehen,
                Öffnungszeiten prüfen, Fotos betrachten und einen Tisch
                reservieren.
              </p>

              <p className="seoHero__text">
                Social Media, Google und Bewertungsplattformen sind wichtig.
                Aber Ihre eigene Website ist der Bereich, den Ihr Restaurant
                kontrolliert: Inhalte, Speisekarte, Bilder, Angebote,
                Reservierung und Kontakt.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="de" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Inhalt des Artikels</h2>

        <nav className="qrArticle__nav" aria-label="Artikelnavigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="check" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Braucht Ihr Restaurant eine bessere Website?</h2>

            <p>
              Wählen Sie die Aussagen aus, die zu Ihrem Restaurant passen. Das
              Ergebnis ist bewusst ehrlich: Nicht jedes Restaurant braucht sofort
              eine große Website. Aber fast jedes Restaurant profitiert von einem
              klaren Ort für Speisekarte, Öffnungszeiten, Kontakt und
              Reservierung.
            </p>

            <div className="qrArticle__checkGrid">
              {checklist.map((item) => (
                <label key={item} className="qrArticle__checkItem">
                  <input
                    type="checkbox"
                    checked={checked.includes(item)}
                    onChange={() => toggle(item)}
                  />
                  <span className="text">{item}</span>
                </label>
              ))}
            </div>

            <div className={`qrArticle__result qrArticle__result--${result.tone}`}>
              <div className="qrArticle__score">
                {score} / {checklist.length}
              </div>

              <div>
                <strong>{result.title}</strong>
                <p>{result.text}</p>
              </div>
            </div>
          </div>
        </section>

        <section id="definition" className="acquaintance">
          <h2>Was sollte eine Restaurant-Website heute leisten?</h2>

          <p>
            Eine gute Restaurant-Website beantwortet die wichtigsten Fragen der
            Gäste schnell: Was kann ich dort essen? Wo befindet sich das
            Restaurant? Wann ist es geöffnet? Wie kann ich einen Tisch
            reservieren?
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Digitale Speisekarte.</li>
            <li className="text__seo-li">Öffnungszeiten.</li>
            <li className="text__seo-li">Adresse und Kontakt.</li>
            <li className="text__seo-li">Fotos von Speisen und Atmosphäre.</li>
            <li className="text__seo-li">Reservierung.</li>
            <li className="text__seo-li">QR-Code-Zugang.</li>
            <li className="text__seo-li">Aktuelle Angebote.</li>
            <li className="text__seo-li">Mobile Ansicht.</li>
          </ul>

          <p>
            Die beste Restaurant-Website ist oft nicht die komplizierteste. Es
            ist die Website, die Gästen hilft, schnell eine Entscheidung zu
            treffen.
          </p>
        </section>

        <section id="visibility" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Warum Online-Sichtbarkeit für Restaurants wichtig ist</h2>

            <p>
              Viele Gäste entdecken Restaurants online, bevor sie überhaupt vor
              der Tür stehen: über Google, Karten, soziale Medien, Empfehlungen
              oder eine direkte Suche nach Speisekarte, Öffnungszeiten oder
              Reservierung.
            </p>

            <h3>Ihre Website kann zum zentralen Ziel werden</h3>

            <p>
              Eine Website ist nicht der einzige Kanal, aber sie kann der
              zentrale Ort sein, auf den andere Kanäle verweisen: Google-Profil,
              QR-Code, soziale Medien, Flyer oder digitale Speisekarte.
            </p>

            <p>
              So finden Gäste eine klare Quelle, statt zwischen verschiedenen
              Plattformen, veralteten PDF-Dateien, Social-Media-Beiträgen und
              einzelnen Kontaktlinks zu springen.
            </p>
          </div>
        </section>

        <section id="menu" className="acquaintance">
          <h2>
            Die Speisekarte ist oft der wichtigste Teil einer Restaurant-Website
          </h2>

          <p>
            Viele Besucher kommen mit einer sehr konkreten Frage auf eine
            Restaurant-Website: Was kann ich dort essen und was kostet es?
          </p>

          <p>
            Deshalb sollte die Speisekarte nicht versteckt, veraltet oder nur
            als schwer lesbare PDF verfügbar sein. Eine mobile digitale
            Speisekarte mit Kategorien, Bildern und aktuellen Preisen kann viel
            klarer sein.
          </p>

          <div className="seoCompareSmall">
            <div>
              <strong>Verwandte Seite</strong>
              <p>
                <Link to="/de/digitale-speisekarte">
                  Digitale Speisekarte für Restaurants
                </Link>
              </p>
            </div>

            <div>
              <strong>Verwandte Seite</strong>
              <p>
                <Link to="/de/qr-code-speisekarte">
                  QR-Code-Speisekarte für Restaurants
                </Link>
              </p>
            </div>
          </div>
        </section>

        <section id="trust" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Eine Website schafft Vertrauen, bevor der Gast ankommt</h2>

            <p>
              Gäste entscheiden nicht nur nach Preis und Entfernung. Sie achten
              auch auf den Eindruck, darauf, wie aktuell Informationen wirken,
              auf Fotos, Klarheit und darauf, ob das Restaurant professionell
              erscheint.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Aktuelle Speisekarte.</li>
              <li className="text__seo-li">Echte Fotos.</li>
              <li className="text__seo-li">Klare Öffnungszeiten.</li>
              <li className="text__seo-li">Kontaktinformationen.</li>
              <li className="text__seo-li">Reservierungsmöglichkeit.</li>
              <li className="text__seo-li">Starke mobile Erfahrung.</li>
            </ul>

            <p>
              Wenn diese Informationen fehlen oder veraltet sind, können Gäste
              unsicher werden. Eine gute Website reduziert diese Unsicherheit.
            </p>

            <VisualIdea>
              Eine Vorschau einer Restaurant-Website auf einem Smartphone:
              Speisekarte, Fotos, Öffnungszeiten und ein klarer
              Reservierungsbutton an einem Ort.
            </VisualIdea>
          </div>
        </section>

        <section id="reservation" className="acquaintance">
          <h2>Website, Speisekarte und Reservierung sollten zusammenarbeiten</h2>

          <p>
            Eine Restaurant-Website wird stärker, wenn sie nicht nur informiert,
            sondern auch den nächsten Schritt einfach macht. Ein Gast sieht die
            Speisekarte, bekommt Interesse und kann direkt einen Tisch
            reservieren.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">Restaurant finden.</li>
            <li className="text__seo-li">Speisekarte ansehen.</li>
            <li className="text__seo-li">Atmosphäre prüfen.</li>
            <li className="text__seo-li">Tisch reservieren.</li>
          </ol>

          <p>
            Dieser Weg ist einfacher, als zwischen verschiedenen Plattformen,
            PDFs, Nachrichten und Telefonnummern zu springen.
          </p>

          <div className="seoCompareSmall">
            <div>
              <strong>Verwandte Seite</strong>
              <p>
                <Link to="/de/online-tischreservierung">
                  Online-Tischreservierung
                </Link>
              </p>
            </div>

            <div>
              <strong>Verwandte Seite</strong>
              <p>
                <Link to="/de/kostenlose-digitale-speisekarte">
                  Kostenlose digitale Speisekarte
                </Link>
              </p>
            </div>
          </div>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Häufige Fehler bei Restaurant-Websites</h2>

            <p>
              Eine Restaurant-Website muss nicht groß sein, aber sie muss die
              wichtigsten Fragen der Gäste klar beantworten.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Häufige Fehler</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Alte Preise oder nicht verfügbare Gerichte bleiben online.
                  </li>
                  <li className="text__seo-li">
                    Die Speisekarte existiert nur als PDF.
                  </li>
                  <li className="text__seo-li">
                    Gäste finden nicht leicht, wie sie reservieren können.
                  </li>
                  <li className="text__seo-li">
                    Die mobile Ansicht ist schwach.
                  </li>
                  <li className="text__seo-li">
                    Adresse, Öffnungszeiten oder Kontaktdaten sind schwer zu
                    finden.
                  </li>
                  <li className="text__seo-li">
                    Website, QR-Code und digitale Speisekarte sind nicht
                    verbunden.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Besser</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Speisekarte aktuell halten.
                  </li>
                  <li className="text__seo-li">
                    Eine mobile digitale Speisekarte nutzen.
                  </li>
                  <li className="text__seo-li">
                    Einen klaren Reservierungsweg hinzufügen.
                  </li>
                  <li className="text__seo-li">
                    Die Website auf Smartphones komfortabel machen.
                  </li>
                  <li className="text__seo-li">
                    Öffnungszeiten, Adresse und Kontakt klar zeigen.
                  </li>
                  <li className="text__seo-li">
                    Website, QR-Code und Speisekarte in einem Ablauf verbinden.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="small" className="acquaintance">
          <h2>Brauchen auch kleine Restaurants eine Website?</h2>

          <p>
            Kleine Restaurants denken oft, dass soziale Medien oder Google
            ausreichen. Diese Kanäle sind wichtig, aber sie gehören dem
            Restaurant nicht vollständig. Eine Website gibt mehr Kontrolle.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Nur externe Plattformen</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Weniger Kontrolle.</li>
                <li className="text__seo-li">
                  Abhängig von Layout und Regeln anderer Plattformen.
                </li>
                <li className="text__seo-li">
                  Informationen sind oft über mehrere Kanäle verteilt.
                </li>
                <li className="text__seo-li">
                  Speisekarte und Reservierung sind nicht immer verbunden.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Eigene Restaurant-Website</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Zentraler Ort für Gäste.</li>
                <li className="text__seo-li">Eigene digitale Speisekarte.</li>
                <li className="text__seo-li">Klare Reservierung.</li>
                <li className="text__seo-li">Zielseite für den QR-Code.</li>
                <li className="text__seo-li">
                  Besser kontrollierte Darstellung.
                </li>
              </ul>
            </div>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="de" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>Häufige Fragen zu Restaurant-Websites</h2>

          <div className="seoFaq">
            <details>
              <summary>Braucht jedes Restaurant eine eigene Website?</summary>
              <p>
                Nicht jedes Restaurant braucht sofort eine große Website. Aber
                fast jedes Restaurant profitiert von einem klaren Ort für
                Speisekarte, Öffnungszeiten, Kontakt und Reservierung.
              </p>
            </details>

            <details>
              <summary>Reichen soziale Medien aus?</summary>
              <p>
                Soziale Medien sind nützlich, aber nicht vollständig
                kontrollierbar. Eine Website ist stabiler als zentraler Ort für
                wichtige Informationen.
              </p>
            </details>

            <details>
              <summary>Welche Inhalte sind am wichtigsten?</summary>
              <p>
                Für viele Gäste sind Speisekarte, Öffnungszeiten, Adresse,
                Kontakt, Fotos und Reservierung die wichtigsten Inhalte.
              </p>
            </details>

            <details>
              <summary>Sollte die Speisekarte als PDF eingebunden werden?</summary>
              <p>
                Eine PDF ist möglich, aber auf Smartphones oft unbequem. Eine
                mobile digitale Speisekarte ist meistens nutzerfreundlicher.
              </p>
            </details>

            <details>
              <summary>Kann eine Website mit einem QR-Code verbunden werden?</summary>
              <p>
                Ja. Ein QR-Code kann Gäste direkt zur Speisekarte oder zur
                Restaurantseite führen.
              </p>
            </details>

            <details>
              <summary>Kann ich klein starten?</summary>
              <p>
                Ja. Ein guter Start kann Speisekarte, QR-Code, Kontakt und eine
                einfache Restaurantseite enthalten.
              </p>
            </details>
          </div>
        </section>
        <SeoRelatedLinks
          lang="de"
          group="business"
          exclude="/de/restaurant-website"
        />
      </article>
    </main>
  );
}